import { Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { getLocale } from "../../../i18n";
import { useTranslation } from "../../../i18n/useTranslation";
import { DeviceService } from "../../../services/device.service";

type EvidenceRequest = {
  _id: string;
  from: string;
  to: string;
  employee?: string;
  flaggedOnly?: boolean;
  status: "requested" | "uploaded" | "failed";
  requestedAt?: string;
  completedAt?: string | null;
  matchedCount?: number;
  uploadedCount?: number;
  failedCount?: number;
  errorMessage?: string;
  mqttPublished?: boolean;
};

type Props = {
  device: { deviceId: string; name?: string } | null;
  onClose: () => void;
};

const pad = (n: number) => String(n).padStart(2, "0");
const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
const ymd = (iso: string) => toLocalInput(new Date(iso)).slice(0, 10);

const fmt = (value?: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
  });
};

/** Portal pull of evidence clips — same UX as the attendance-log request dialog. */
const EvidencePullDialog = ({ device, onClose }: Props) => {
  const { t } = useTranslation();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [employee, setEmployee] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [items, setItems] = useState<EvidenceRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [message, setMessage] = useState("");

  const refresh = useCallback(async (deviceId: string) => {
    setLoading(true);
    try {
      const result = await DeviceService.listEvidenceRequests(deviceId);
      setItems(result.data?.data || []);
    } catch (error) {
      console.error(error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!device) return;
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    setFrom(toLocalInput(start));
    setTo(toLocalInput(now));
    setEmployee("");
    setFlaggedOnly(false);
    setMessage("");
    refresh(device.deviceId);
  }, [device, refresh]);

  const submit = async () => {
    if (!device) return;
    const f = new Date(from);
    const tt = new Date(to);
    if (
      Number.isNaN(f.getTime()) ||
      Number.isNaN(tt.getTime()) ||
      tt <= f ||
      tt.getTime() - f.getTime() > 7 * 24 * 3600 * 1000
    ) {
      setMessage(t("devices.evidencePullRange"));
      return;
    }
    setRequesting(true);
    setMessage("");
    try {
      const result = await DeviceService.requestEvidence(device.deviceId, {
        from: f.toISOString(),
        to: tt.toISOString(),
        employee: employee.trim() || undefined,
        flaggedOnly,
      });
      const data = result.data?.data;
      setMessage(
        data?.mqttPublished
          ? t("devices.evidencePullRequested")
          : `${t("devices.evidencePullRequested")} (${t("devices.attendanceLogsMqttPending")})`
      );
      await refresh(device.deviceId);
    } catch (error) {
      console.error(error);
      setMessage(t("devices.evidencePullFailed"));
    } finally {
      setRequesting(false);
    }
  };

  const statusLabel = (status: string) => {
    if (status === "uploaded") return t("devices.attendanceLogsStatusUploaded");
    if (status === "failed") return t("devices.attendanceLogsStatusFailed");
    return t("devices.attendanceLogsStatusRequested");
  };

  const viewHref = (item: EvidenceRequest) => {
    const q = new URLSearchParams({
      deviceId: device?.deviceId || "",
      from: ymd(item.from),
      to: ymd(item.to),
    });
    if (item.employee) q.set("employee", item.employee);
    return `/attendance-evidence?${q.toString()}`;
  };

  return (
    <Dialog open={Boolean(device)} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>
        {t("devices.evidencePullTitle", { name: device?.name || device?.deviceId || "" })}
      </DialogTitle>
      <DialogContent>
        <div className='devices-listing__logs-toolbar'>
          <TextField
            type='datetime-local'
            size='small'
            label={t("devices.evidencePullFrom")}
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            type='datetime-local'
            size='small'
            label={t("devices.evidencePullTo")}
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <TextField
            size='small'
            label={t("devices.evidencePullEmployee")}
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox checked={flaggedOnly} onChange={(e) => setFlaggedOnly(e.target.checked)} />
            }
            label={t("devices.evidencePullFlagged")}
          />
          <Button variant='contained' disabled={requesting || !from || !to} onClick={submit}>
            {t("devices.evidencePullRequest")}
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            disabled={loading || !device}
            onClick={() => device && refresh(device.deviceId)}
          >
            {t("devices.attendanceLogsRefresh")}
          </Button>
        </div>
        {message ? <div className='devices-listing__logs-message'>{message}</div> : null}
        {loading ? (
          <div style={{ padding: "1.6rem 0" }}>...</div>
        ) : items.length === 0 ? (
          <div className='devices-listing__logs-empty'>{t("devices.evidencePullEmpty")}</div>
        ) : (
          <div className='devices-listing__logs-list'>
            {items.map((item) => (
              <div className='devices-listing__logs-item' key={item._id}>
                <div>
                  <strong>{statusLabel(item.status)}</strong>
                  <div className='devices-listing__logs-meta'>
                    {fmt(item.from)} → {fmt(item.to)}
                    {item.employee ? ` · ${item.employee}` : ""}
                    {item.flaggedOnly ? ` · ${t("devices.evidencePullFlagged")}` : ""}
                  </div>
                  <div className='devices-listing__logs-meta'>
                    {item.status === "requested"
                      ? `${fmt(item.requestedAt)}${
                          item.mqttPublished ? ` · ${t("devices.attendanceLogsMqtt")}` : ""
                        }`
                      : `${t("devices.evidencePullResult", {
                          matched: item.matchedCount || 0,
                          uploaded: item.uploadedCount || 0,
                          failed: item.failedCount || 0,
                        })} · ${fmt(item.completedAt)}`}
                  </div>
                  {item.errorMessage ? (
                    <div className='devices-listing__logs-error'>{item.errorMessage}</div>
                  ) : null}
                </div>
                <Button
                  size='small'
                  variant='outlined'
                  disabled={!item.uploadedCount}
                  href={viewHref(item)}
                >
                  {t("devices.evidencePullView")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvidencePullDialog;
